import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import type { FramesManager } from 'shared-libs/components/view/frames-manager'
import type {
  RadioButtonStyle,
  RadioOption,
  RadioOptionLayout,
  UI2RadioButtonFieldProps,
} from 'shared-libs/components/ui2/fields/ui2-radio-button-field/ui2-radio-button-field-props'
import { UI2_RADIO_BUTTON_FIELD_DEFAULT_PROPS } from 'shared-libs/components/ui2/fields/ui2-radio-button-field/ui2-radio-button-field-props'
import {
  getEffectiveTranslationTable,
  getOptionIdentity,
  getOptionLabel as translateOptionLabel,
  getStoredValueForOption,
  isOptionSelected,
  resolveSelectOptions,
} from 'shared-libs/components/ui2/fields/core-field/selectable-field-props'

import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import { UI2NoValueAvailable } from '../field-ui/no-value-available/no-value-available'
import { IUI2FieldShellProps, UI2FieldShell } from '../field-ui/field-shell/field-shell'
import './_ui2-radio-button-field.scss'
import type { IconValue } from 'browser/components/atomic-elements/atoms/icon/icon'
import { Icon } from 'browser/components/atomic-elements/atoms/icon/icon'

interface IUI2RadioWebExtras {
  onChange?: (value: any) => void
  className?: string
  style?: React.CSSProperties
  frames?: FramesManager
  errors?: any[]
}

/**
 * @uiComponent
 */
export interface IUI2RadioButtonFieldProps extends UI2RadioButtonFieldProps, IUI2RadioWebExtras {}

export class UI2RadioButtonField extends React.Component<IUI2RadioButtonFieldProps> {
  public static defaultProps: Partial<IUI2RadioButtonFieldProps> = {
    ...UI2_RADIO_BUTTON_FIELD_DEFAULT_PROPS,
  }

  public render() {
    const { className } = this.props
    const options = this.getTranslatedOptions()
    const buttonStyle = this.props.buttonStyle ?? 'default'
    const isMulti = !!this.props.isMultiSelect
    const isPill = buttonStyle === 'pill'
    const isSegmented = isPill && !isMulti && this.props.pillVariant !== 'standalone'

    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        className={classNames('ui2-radio-button-field', className)}
        renderReadOnly={(_, helpers) => this.renderReadOnly(options, helpers)}
        // Body is a UI2FieldControl, which slots Cancel beside the input.
        bodyIsFieldControl={true}
      >
        <UI2FieldControl
          className={classNames('ui2-radio-button-field__control', {
            'ui2-radio-button-field__control--segmented': isSegmented,
            'ui2-radio-button-field__control--frameless': !isPill,
          })}
        >
          {this.renderOptions(options, buttonStyle, isMulti, isSegmented)}
        </UI2FieldControl>
      </UI2FieldShell>
    )
  }

  private renderOptions(
    options: RadioOption[],
    buttonStyle: RadioButtonStyle,
    isMulti: boolean,
    isSegmented: boolean,
  ): React.ReactNode {
    const { isDisabled } = this.props
    if (_.isEmpty(options)) return null

    const layout = this.getOptionLayout()
    const isPill = buttonStyle === 'pill'

    return (
      <div
        className={classNames('ui2-radio-button-field__options', {
          'ui2-radio-button-field__options--horizontal': layout === 'horizontal',
          'ui2-radio-button-field__options--vertical': layout === 'vertical',
          'ui2-radio-button-field__options--pill': isPill,
          'ui2-radio-button-field__options--default': !isPill,
          'ui2-radio-button-field__options--segmented': isSegmented,
          'ui2-radio-button-field__options--disabled': isDisabled,
        })}
        role={isMulti ? 'group' : 'radiogroup'}
        aria-label={this.props.label || this.props.accessibilityLabel}
      >
        {options.map((option, index) =>
          this.renderOption(option, index, isPill, isMulti, isSegmented),
        )}
      </div>
    )
  }

  private renderOption(
    option: RadioOption,
    index: number,
    isPill: boolean,
    isMulti: boolean,
    isSegmented: boolean,
  ): React.ReactNode {
    const { isDisabled, testID, value, isMultiSelect } = this.props
    const isSelected = isOptionSelected(option, value, isMultiSelect)
    const role = isMulti ? 'checkbox' : 'radio'

    if (isPill) {
      const optionIcon = !isSegmented ? option.icon : undefined
      const colorFamily = !isSegmented ? option.selectedColorFamily : undefined
      return (
        <button
          key={index}
          type='button'
          role={role}
          aria-checked={isSelected}
          disabled={!!isDisabled}
          onClick={() => this.handleToggle(option)}
          data-testid={testID ? `${testID}-option-${index}` : undefined}
          className={classNames('ui2-radio-button-field__pill', {
            'ui2-radio-button-field__pill--segmented': isSegmented,
            'ui2-radio-button-field__pill--standalone': !isSegmented,
            'ui2-radio-button-field__pill--selected': isSelected,
            [`ui2-radio-button-field__pill--color-${colorFamily}`]: !!colorFamily,
          })}
        >
          {!!optionIcon && (
            <Icon name={optionIcon as IconValue} size={14} />
          )}
          <span className='ui2-radio-button-field__pill-label'>{option.label}</span>
        </button>
      )
    }

    return (
      <label
        key={index}
        className={classNames('ui2-radio-button-field__default', {
          'ui2-radio-button-field__default--selected': isSelected,
          'ui2-radio-button-field__default--disabled': isDisabled,
        })}
      >
        <input
          type={isMulti ? 'checkbox' : 'radio'}
          checked={isSelected}
          disabled={!!isDisabled}
          onChange={() => this.handleToggle(option)}
          className='ui2-radio-button-field__native-input'
          aria-label={option.label}
        />
        {isMulti ? this.renderCheckboxBox(isSelected) : this.renderBullet(isSelected)}
        <span className='ui2-radio-button-field__default-label'>{option.label}</span>
      </label>
    )
  }

  private renderBullet(isSelected: boolean): React.ReactNode {
    return (
      <span
        className={classNames('ui2-radio-button-field__bullet', {
          'ui2-radio-button-field__bullet--selected': isSelected,
        })}
        aria-hidden='true'
      >
        {isSelected && <span className='ui2-radio-button-field__bullet-inner' />}
      </span>
    )
  }

  private renderCheckboxBox(isChecked: boolean): React.ReactNode {
    return (
      <span
        className={classNames('ui2-radio-button-field__checkbox', {
          'ui2-radio-button-field__checkbox--checked': isChecked,
        })}
        aria-hidden='true'
      >
        {isChecked && (
          <svg className='ui2-radio-button-field__checkmark' viewBox='0 0 16 16'>
            <polyline
              points='3.5,8.5 6.5,11.5 12.5,5.5'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )}
      </span>
    )
  }

  private renderReadOnly(options: RadioOption[], { editNode }: { editNode?: React.ReactNode } = {}): React.ReactNode {
    const { value, isMultiSelect } = this.props
    const labels = options
      .filter((o) => isOptionSelected(o, value, isMultiSelect))
      .map((o) => o.label)
    let content: React.ReactNode
    if (labels.length) {
      content = <span className='ui2-radio-button-field__read-only-text'>{labels.join(', ')}</span>
    } else if (!_.isNil(value) && String(value) !== '') {
      content = <span className='ui2-radio-button-field__read-only-text'>{String(value)}</span>
    } else {
      content = <UI2NoValueAvailable />
    }
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>{content}</div>
        {editNode}
      </>
    )
  }

  private handleToggle = (option: RadioOption): void => {
    const { isMultiSelect, value, onChange, isDisabled } = this.props
    if (isDisabled || !onChange) return

    const writable = getStoredValueForOption(option, this.props)
    if (isMultiSelect) {
      const current = _.isArray(value) ? value.slice() : []
      const optionId = getOptionIdentity(option.value)
      const existingIndex = current.findIndex(
        (v: any) => _.isEqual(getOptionIdentity(v), optionId),
      )
      if (existingIndex >= 0) {
        current.splice(existingIndex, 1)
      } else {
        current.push(writable)
      }
      onChange(current)
    } else {
      const isSame = _.isEqual(getOptionIdentity(value), getOptionIdentity(option.value))
      onChange(isSame ? undefined : writable)
    }
  }

  private getTranslatedOptions(): RadioOption[] {
    const translationTable = getEffectiveTranslationTable(this.props)
    return resolveSelectOptions(this.props).map((opt) => ({
      ...(opt as RadioOption),
      label: translateOptionLabel(opt, translationTable),
    }))
  }

  private getOptionLayout(): RadioOptionLayout {
    const { optionLayout, buttonStyle } = this.props
    if (optionLayout) return optionLayout
    return buttonStyle === 'pill' ? 'horizontal' : 'vertical'
  }
}
